/**
 * ToolkitUsageTile — super-admin dashboard card showing which Demo
 * Toolkit features got used in the selected window, ranked by count.
 * Powered by `/api/analytics/toolkit/usage`.
 *
 * Answers:
 *   - "Which toolkit features actually get used in live demos?"
 *   - "Has anyone opened the Demo Story Director in the last 30 days?"
 *   - "Is the new Rehearse button getting traction?"
 *
 * Unused actions render with `count=0` + grey styling so PMs can
 * instantly see the idle roster and decide whether to promote,
 * deprecate, or reskin them.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Boxes, Users } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';


const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];


function formatRelative(iso) {
  if (!iso) return 'Never used';
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}


export default function ToolkitUsageTile() {
  const [windowDays, setWindowDays] = useState(30);
  const { data, loading, error } = useTelemetryTile({
    url: `/analytics/toolkit/usage?window_days=${windowDays}`,
    deps: [windowDays],
  });

  if (error === 'forbidden') return null;

  const usage = data?.usage || [];
  const totals = data?.totals || { events: 0, distinct_actions_used: 0, roster_size: 0 };
  const maxCount = Math.max(1, ...usage.map((u) => u.count));

  return (
    <Card data-testid="toolkit-usage-tile" className="border-indigo-200 bg-gradient-to-br from-white to-indigo-50/40">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Boxes className="h-4 w-4 text-indigo-500" />
              Toolkit Feature Usage
            </CardTitle>
            <CardDescription className="mt-0.5">
              Which Demo Toolkit actions get used in live demos vs sit idle. Idle features are candidates for promotion, reskin, or deprecation.
            </CardDescription>
          </div>
          <div className="inline-flex bg-white border border-slate-200 rounded-md p-0.5" data-testid="toolkit-usage-window-selector">
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setWindowDays(o.days)}
                className={
                  'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                  (windowDays === o.days ? 'bg-indigo-500 text-white' : 'text-slate-600 hover:bg-slate-100')
                }
                data-testid={`toolkit-usage-window-${o.days}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="toolkit-usage-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="toolkit-usage-error">
            Couldn't load toolkit usage.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Events</p>
                <p className="text-lg font-bold text-indigo-700 tabular-nums">{totals.events}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Active features</p>
                <p className="text-lg font-bold text-emerald-700 tabular-nums">
                  {totals.distinct_actions_used}<span className="text-xs text-slate-400">/{totals.roster_size}</span>
                </p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Idle features</p>
                <p className="text-lg font-bold text-slate-400 tabular-nums">
                  {totals.roster_size - totals.distinct_actions_used}
                </p>
              </div>
            </div>

            <ol className="space-y-1" data-testid="toolkit-usage-list">
              {usage.map((row) => {
                const pct = row.count / maxCount;
                const isIdle = row.count === 0;
                return (
                  <li
                    key={row.action}
                    className={`flex items-center gap-2 ${isIdle ? 'opacity-50' : ''}`}
                    data-testid={`toolkit-usage-row-${row.action}`}
                  >
                    <span className="w-32 text-[11px] font-mono text-slate-700 truncate">
                      {row.action}
                    </span>
                    <div className="relative flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-400 to-indigo-500 transition-all"
                        style={{ width: `${Math.round(pct * 100)}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-[11px] font-semibold text-indigo-700 tabular-nums">
                      {row.count}
                    </span>
                    {row.unique_users > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 w-10 text-[10px] text-slate-500 tabular-nums"
                        title={`${row.unique_users} unique user${row.unique_users === 1 ? '' : 's'}`}
                      >
                        <Users className="h-2.5 w-2.5" />
                        {row.unique_users}
                      </span>
                    )}
                    <span className="w-16 text-right text-[10px] text-slate-400">
                      {formatRelative(row.last_used)}
                    </span>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </CardContent>
    </Card>
  );
}
